import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#051c14] text-gray-100 font-sans selection:bg-[#34d399] selection:text-black">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#051c14]/80 backdrop-blur-md px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <span>Back to Mahjong Blast</span>
          </Link>
          <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-wider text-gray-400">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <span>·</span>
            <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">
              Corvas Studio <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <section className="text-center py-6">
          <p className="text-xs font-mono tracking-widest uppercase text-[#34d399] mb-2">Legal Terms &amp; User License</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Terms of Use
          </h1>
          <p className="text-sm text-gray-400">
            Application: <strong>Mahjong Blast: 3D Tile Match</strong> · Effective Date: March 1, 2026<br />
            Developer &amp; Publisher: <strong>Corvas</strong>
          </p>
        </section>

        {/* 1. Agreement */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">1. Agreement to Terms</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            By downloading, installing, accessing, or using <strong>Mahjong Blast: 3D Tile Match</strong> (the "Application"), you agree to be bound by these Terms of Use and all applicable laws. If you disagree with any part of these terms, you must discontinue use and uninstall the Application immediately.
          </p>
        </section>

        {/* 2. License */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">2. License Grant &amp; Permitted Use</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            We grant you a non-exclusive, non-transferable, revocable limited license to install and run the Application on authorized Android devices for personal, non-commercial, or creator production purposes in accordance with these Terms.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
            <li>You must not reverse engineer, decompile, or extract source assets from the Application.</li>
            <li>You must not circumvent license checks, security protections, or ad mechanisms.</li>
            <li>You must comply with all community and copyright guidelines when producing content.</li>
          </ul>
        </section>

        {/* 3. Subscriptions */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">3. In-App Purchases &amp; Subscriptions</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            In-app purchases include consumables (power-up bundles) and non-consumables (ad-free unlock). Virtual coins and power-ups have no real monetary value, cannot be redeemed for fiat currency, and purchases are governed by Google Play&#039;s refund terms.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            All billing, recurring renewals, and cancellations are managed directly through Google Play Store subscription settings. Refunds are handled strictly under Google Play refund policies.
          </p>
        </section>

        {/* 4. Product Disclaimers */}
        
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">4. Specific Product Disclaimers</h2>
          <div className="space-y-4 pt-2">
            
          <div className="border-t border-white/5 pt-4 first:border-none first:pt-0" key="Virtual Goods &amp; Entertainment">
            <h4 className="text-base font-semibold text-[#34d399] mb-1">Virtual Goods &amp; Entertainment</h4>
            <p className="text-sm text-gray-300 leading-relaxed">Mahjong Blast is a skill-based puzzle game intended for entertainment purposes only. In-game power-ups, coins, and streak badges possess no cash value and do not constitute gambling or wagering.</p>
          </div>
  
          </div>
        </section>
        

        {/* 5. Warranties */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">5. Disclaimer of Warranties</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            THE APPLICATION IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW, THE DEVELOPER DISCLAIMS ALL WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
        </section>

        {/* 6. Limitation of Liability */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">6. Limitation of Liability</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL Corvas BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES RESULTING FROM YOUR USE OR INABILITY TO USE THE APPLICATION.
          </p>
        </section>

        {/* 7. Contact */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-3">
          <h2 className="text-xl font-bold text-white">7. Governing Law &amp; Inquiries</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            These terms are governed by the applicable law of the developer&#039;s registered operating jurisdiction. For questions regarding these Terms, email us at <a href="mailto:corvas.official@gmail.com" className="text-[#34d399] hover:underline">corvas.official@gmail.com</a>.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center text-xs text-gray-500">
        <div className="flex justify-center gap-4 mb-2">
          <Link to="/" className="hover:text-gray-300">Home</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-gray-300">Privacy Policy</Link>
          <span>·</span>
          <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">Corvas Studio</a>
        </div>
        <p>&copy; {new Date().getFullYear()} Corvas. All rights reserved.</p>
      </footer>
    </div>
  );
}
